/*
 * Crafting A-Z — top pages carousel
 *
 * Arrows and dots for a scroll-snap rail. The rail itself is CSS: touch swipe,
 * momentum and snapping are the browser's, so this file only adds the two
 * controls a pointer user needs and keeps them in step with wherever the rail
 * actually is.
 *
 * Everything reads from scrollLeft rather than from a "current index" this file
 * holds, because a swipe moves the rail without going through here. One source
 * of truth means a swipe lights the right dot with no extra bookkeeping.
 *
 * Runs on DOM ready and again on Shopify's section:load, so a merchant adding
 * or reordering cards in the theme editor gets working controls immediately.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-ch-carousel]';

  function slidesOf(carousel) {
    return Array.prototype.slice.call(carousel.querySelectorAll('[data-ch-slide]'));
  }

  /* Scroll position that puts a slide at the rail's left edge.
     Measured against the FIRST slide rather than the track: offsetLeft is taken
     from a shared offsetParent and so carries the track's own padding, which
     would put slide 0 at scrollLeft 2 instead of 0 and leave "prev" one nudge
     short of the start. The difference between two slides cancels it out. */
  function offsetOf(slides, index) {
    return slides[index].offsetLeft - slides[0].offsetLeft;
  }

  /* Index of the slide whose left edge sits closest to the rail's left edge.
     Distance-to-nearest rather than a threshold: mid-swipe the rail can rest
     between two snap points, and the nearest one is always the honest answer. */
  function currentIndex(track, slides) {
    var left = track.scrollLeft;
    var best = 0;
    var bestGap = Infinity;

    for (var i = 0; i < slides.length; i++) {
      var gap = Math.abs(offsetOf(slides, i) - left);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    }

    return best;
  }

  function scrollToIndex(track, slides, index) {
    var clamped = Math.max(0, Math.min(index, slides.length - 1));
    track.scrollTo({ left: offsetOf(slides, clamped), behavior: 'smooth' });
  }

  function init(carousel) {
    if (!carousel || carousel.dataset.chCarouselReady === 'true') return;

    var track = carousel.querySelector('[data-ch-track]');
    var slides = slidesOf(carousel);
    if (!track || slides.length === 0) return;

    var prev = carousel.querySelector('[data-ch-prev]');
    var next = carousel.querySelector('[data-ch-next]');
    /* The dots live outside the carousel wrapper so they can sit under the full
       width of the rail; look for them in the section, falling back to inside. */
    var section = carousel.closest('.ch-top') || carousel;
    var dotsHost = section.querySelector('[data-ch-dots]');

    carousel.dataset.chCarouselReady = 'true';

    var dots = [];
    if (dotsHost) {
      dotsHost.textContent = '';
      slides.forEach(function (slide, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ch-top__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function () {
          scrollToIndex(track, slides, i);
        });
        dotsHost.appendChild(dot);
        dots.push(dot);
      });
    }

    function sync() {
      var index = currentIndex(track, slides);

      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      /* Compare against the real scroll extent, not the index: with several
         cards in view the last card can never reach the left edge, so an
         index-based test would leave "next" enabled at the end of the rail.
         The 2px slack absorbs sub-pixel widths. */
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        scrollToIndex(track, slides, currentIndex(track, slides) - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        scrollToIndex(track, slides, currentIndex(track, slides) + 1);
      });
    }

    var frame = null;
    track.addEventListener(
      'scroll',
      function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          sync();
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', sync, { passive: true });
    sync();
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SELECTOR), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
